
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Canvas, Image as FabricImage } from "fabric";
import { toast } from "@/hooks/use-toast";
import { LoadingIndicator } from "./LoadingIndicator";
import { ScenarioType } from "@/types/analysis";

interface SampleImagesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  canvas: Canvas | null;
  onToolChange: (tool: string) => void;
  onResetAnalysis?: () => void;
}

const sampleImages = [
  {
    id: "21cdbc52-d5d0-4a0b-b3dc-8164b8053d90",
    title: "Sample Patient 1",
    description: "General example for surgical planning",
    scenario_type: "bilobed_flap_nose" as ScenarioType
  },
  {
    id: "62313057-d314-48a9-a55c-2baba6986792",
    title: "Sample Patient 2",
    description: "Example for marking measurements",
    scenario_type: "bilobed_flap_nose" as ScenarioType
  },
  {
    id: "7a09390a-b195-4c33-bc3a-fd668312d593",
    title: "Sample Patient 3",
    description: "Example for surgical planning",
    scenario_type: "bilobed_flap_nose" as ScenarioType
  },
  {
    id: "b5e3a3ae-5840-4356-bbf2-96c4d99dc8b3",
    title: "Sample Patient 4",
    description: "General example for markings",
    scenario_type: "bilobed_flap_nose" as ScenarioType
  },
  {
    id: "bfa593ee-7b0f-4f98-9f8f-8b435a6bd757",
    title: "Sample Patient 5",
    description: "Example for surgical planning",
    scenario_type: "bilobed_flap_nose" as ScenarioType
  },
  {
    id: "c89261f5-24c7-42d3-b9e7-09446bec6bad",
    title: "Sample Patient 6",
    description: "Example for measurements",
    scenario_type: "bilobed_flap_nose" as ScenarioType
  }
];

export const SampleImagesDialog = ({ isOpen, onClose, canvas, onToolChange, onResetAnalysis }: SampleImagesDialogProps) => {
  const handleImageClick = async (sample: typeof sampleImages[0]) => {
    if (!canvas) return;

    try {
      // Reset analysis before adding new scenario
      if (onResetAnalysis) {
        onResetAnalysis();
      }

      const img = await new Promise<FabricImage>((resolve, reject) => {
        const imageUrl = `/lovable-uploads/${sample.id}.png`;
        console.log('Loading sample image:', imageUrl);
        
        FabricImage.fromURL(imageUrl, {
          crossOrigin: 'anonymous'
        }).then((img) => {
          const maxDim = 500; // Changed from 600 to 500
          if (img.width && img.height) {
            if (img.width > maxDim || img.height > maxDim) {
              const scale = maxDim / Math.max(img.width, img.height);
              img.scale(scale);
            }
          }

          // Set the scenario type and add rounded corners
          (img as any).scenario_type = sample.scenario_type;

          img.set({
            left: (canvas.width || 0) / 2 - ((img.width || 0) * (img.scaleX || 1)) / 2,
            top: (canvas.height || 0) / 2 - ((img.height || 0) * (img.scaleY || 1)) / 2,
            rx: 8, // radius for rounded corners
            ry: 8  // radius for rounded corners
          });

          resolve(img);
        }).catch(reject);
      });

      canvas.add(img);
      canvas.setActiveObject(img);
      canvas.renderAll();
      onToolChange("transform"); // Switch to transform tool
      onClose();

      toast({
        title: "Sample image added",
        description: sample.description || "The sample image has been added to your canvas.",
      });
    } catch (error) {
      console.error('Error adding sample image:', error);
      toast({
        variant: "destructive",
        title: "Error adding image",
        description: "There was a problem adding the sample image to your canvas.",
      });
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <DialogContent className="max-w-3xl">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4 p-4">
          {sampleImages.map((sample) => (
            <button
              key={sample.id}
              onClick={() => handleImageClick(sample)}
              className="relative aspect-square overflow-hidden rounded-lg border border-gray-200 hover:border-gray-300 transition-all hover:shadow-md"
            >
              <img
                src={`/lovable-uploads/${sample.id}.png`}
                alt={sample.description || sample.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute bottom-0 left-0 right-0 bg-black/50 text-white text-xs p-2">
                {sample.title}
              </div>
            </button>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};
